import { type BreadcrumbItem, createBreadcrumbList } from '../seo-breadcrumbs';
import { BASE_URL } from '../sitemap-pages';
import { CATEGORY_LABELS, LEVEL_LABELS } from './blog-categories';
import type { BlogPost } from './blog-types';

const SITE_LOGO_URL = `${BASE_URL}/images/russlogo-200x200-black-bg.webp`;

export const BLOG_AUTHOR_PERSON: Readonly<Record<string, unknown>> = {
  '@type': 'Person',
  name: 'Russ Rodgers',
  jobTitle: 'Bass Guitar Instructor since 1985',
  url: `${BASE_URL}/about/bio`,
  worksFor: {
    '@type': 'EducationalOrganization',
    name: 'Russ Rodgers Bass Guitar Instruction',
    url: BASE_URL,
  },
};

const BLOG_PUBLISHER: Readonly<Record<string, unknown>> = {
  '@type': 'EducationalOrganization',
  name: 'Russ Rodgers Bass Guitar Instruction',
  url: BASE_URL,
  logo: {
    '@type': 'ImageObject',
    url: SITE_LOGO_URL,
  },
};

/** Returns the OG/schema thumbnail URL for a YouTube video ID. */
export function youtubeThumbnailUrl(
  videoId: string,
  quality: 'maxresdefault' | 'hqdefault' = 'maxresdefault',
): string {
  return `https://i.ytimg.com/vi/${videoId}/${quality}.jpg`;
}

/** Resolves the post's primary image URL (absolute). */
export function resolvePostImage(post: BlogPost): string {
  if (post.ogImage) {
    return post.ogImage.startsWith('http')
      ? post.ogImage
      : `${BASE_URL}${post.ogImage}`;
  }
  if (post.youtubeId) {
    return youtubeThumbnailUrl(post.youtubeId);
  }
  return SITE_LOGO_URL;
}

export function blogPostUrl(slug: string): string {
  return `${BASE_URL}/blog/${slug}`;
}

export function buildBlogPostingSchema(
  post: BlogPost,
): Record<string, unknown> {
  const url = blogPostUrl(post.slug);
  return {
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    datePublished: post.datePublished,
    dateModified: post.dateModified ?? post.datePublished,
    inLanguage: 'en-US',
    image: resolvePostImage(post),
    wordCount: post.wordCount,
    articleSection: CATEGORY_LABELS[post.category] ?? post.category,
    keywords: [...post.tags, LEVEL_LABELS[post.level] ?? post.level].join(', '),
    author: BLOG_AUTHOR_PERSON,
    publisher: BLOG_PUBLISHER,
  };
}

/** ONE list for the post page's visible trail AND its BreadcrumbList JSON-LD. */
export function blogPostBreadcrumbItems(post: BlogPost): BreadcrumbItem[] {
  return [
    { name: 'Home', path: '/' },
    { name: 'Blog', path: '/blog' },
    { name: post.title },
  ];
}

export function buildBlogPostBreadcrumb(
  post: BlogPost,
): Record<string, unknown> {
  return createBreadcrumbList(blogPostBreadcrumbItems(post));
}

export function buildFaqSchema(post: BlogPost): Record<string, unknown> | null {
  if (!post.faq || post.faq.length === 0) return null;
  return {
    '@type': 'FAQPage',
    mainEntity: post.faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

export function buildVideoObjectSchema(
  post: BlogPost,
): Record<string, unknown> | null {
  if (!post.youtubeId) return null;
  const schema: Record<string, unknown> = {
    '@type': 'VideoObject',
    name: post.title,
    description: post.excerpt,
    thumbnailUrl: youtubeThumbnailUrl(post.youtubeId, 'hqdefault'),
    uploadDate: post.videoUploadDate ?? post.datePublished,
    contentUrl: `https://www.youtube.com/watch?v=${post.youtubeId}`,
    embedUrl: `https://www.youtube.com/embed/${post.youtubeId}`,
  };
  if (post.transcript) {
    schema['transcript'] = post.transcript;
  }
  return schema;
}

/**
 * Full graph of route-specific schemas for a post page. Consumers pass this
 * into SEOService.setRouteSchemas so it slots in after [Organization, WebPage].
 */
export function buildBlogPostSchemas(
  post: BlogPost,
): Record<string, unknown>[] {
  const schemas: Record<string, unknown>[] = [
    buildBlogPostBreadcrumb(post),
    buildBlogPostingSchema(post),
  ];
  const video = buildVideoObjectSchema(post);
  if (video) schemas.push(video);
  const faq = post.type === 'front-door' ? buildFaqSchema(post) : null;
  if (faq) schemas.push(faq);
  return schemas;
}
